import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FaEdit, FaTrashAlt, FaTrophy } from "react-icons/fa";

export interface MatchCardPlayer {
  id: number;
  name: string;
}

export interface MatchCardData {
  id: number;
  match_date: string | Date;
  teama: number[];
  teamb: number[];
  scorea: number | null;
  scoreb: number | null;
  winner?: "A" | "B" | "Draw";
}

interface MatchCardProps {
  match: MatchCardData;
  players: MatchCardPlayer[];
  onEdit?: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
}

const getName = (players: MatchCardPlayer[], id: number) =>
  players.find((p) => p.id === id)?.name ?? `Player ${id}`;

const MatchCard = ({ match, players, onEdit, onDelete, canEdit = true }: MatchCardProps) => {
  const date =
    match.match_date instanceof Date
      ? match.match_date
      : new Date(match.match_date);

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-medium">{format(date, "MMM d, yyyy")}</p>
          {match.scorea != null && match.scoreb != null ? (
            <p className="text-2xl font-bold">
              {match.scorea} - {match.scoreb}
            </p>
          ) : match.winner === "A" || match.winner === "B" ? (
            <p className="text-lg font-semibold text-soccer-primary">Team {match.winner}</p>
          ) : match.winner === "Draw" ? (
            <p className="text-lg font-semibold text-muted-foreground">Draw</p>
          ) : (
            <p className="text-2xl font-bold text-muted-foreground">—</p>
          )}
        </div>
        {match.winner && match.winner !== "Draw" && (
          <Badge variant="secondary" className="gap-1">
            <FaTrophy className="h-3 w-3" />
            Team {match.winner}
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <p className="font-medium text-muted-foreground mb-1">Team A</p>
          {match.teama.map((id) => (
            <p key={id}>{getName(players, id)}</p>
          ))}
        </div>
        <div>
          <p className="font-medium text-muted-foreground mb-1">Team B</p>
          {match.teamb.map((id) => (
            <p key={id}>{getName(players, id)}</p>
          ))}
        </div>
      </div>
      {canEdit && (onEdit || onDelete) && (
        <div className="flex gap-2 pt-2 border-t">
          {onEdit && (
            <Button size="sm" variant="outline" onClick={onEdit}>
              <FaEdit className="h-3 w-3 mr-1" /> Edit
            </Button>
          )}
          {onDelete && (
            <Button size="sm" variant="outline" className="text-destructive" onClick={onDelete}>
              <FaTrashAlt className="h-3 w-3 mr-1" /> Delete
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default MatchCard;
